import { createContext, type ReactNode, useContext } from 'react'

/**
 * Identifies which AD operational surface the user is currently working in.
 *
 * This is an operational context, not a Boum right. Area entry is still gated
 * by rights, but how a shared component behaves (which cards, actions, and
 * panels it exposes) is derived from the active workspace.
 */
export type AdWorkspace =
  | 'ad-fleet-map'
  | 'remote-assistance-operator'
  | 'rider-support'

interface AdWorkspaceContextType {
  workspace: AdWorkspace
}

const AdWorkspaceContext = createContext<AdWorkspaceContextType | undefined>(
  undefined,
)

export const AdWorkspaceProvider = ({
  workspace,
  children,
}: {
  workspace: AdWorkspace
  children: ReactNode
}) => (
  <AdWorkspaceContext.Provider value={{ workspace }}>
    {children}
  </AdWorkspaceContext.Provider>
)

export const useAdWorkspace = () => {
  const adWorkspaceContextValue = useContext(AdWorkspaceContext)
  if (!adWorkspaceContextValue) {
    throw new Error('useAdWorkspace must be used within an AdWorkspaceProvider')
  }
  return adWorkspaceContextValue
}

/**
 * Workspace for surfaces that may render outside an AdWorkspaceProvider, e.g.
 * Safety Driver Guidance, which reuses AD providers without being an AD
 * operations workspace. Returns undefined instead of throwing.
 */
export const useOptionalAdWorkspace = () =>
  useContext(AdWorkspaceContext)?.workspace
